/**
 * 蓝牙热敏打印机工具
 * 通过 Web Bluetooth API 连接 BLE 热敏打印机，支持 ESC/POS 协议
 * 兼容 Chrome / Edge，浏览器需运行在 HTTPS 或 localhost 环境
 */

const STORAGE_KEY = 'skyPrinterDevice'

const COMMON_SERVICE_UUIDS = [
  0x18f0,
  0xff00,
  0xffe0,
  0xff12,
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  '0000ff00-0000-1000-8000-00805f9b34fb',
]

const MAX_CHUNK_SIZE = 20

export type PrinterChangeListener = (payload: { connected: boolean; deviceName?: string }) => void

interface BluetoothDeviceLike {
  id: string
  name?: string
  gatt?: {
    connected: boolean
    connect(): Promise<any>
    disconnect(): void
    getPrimaryServices(): Promise<any[]>
    getPrimaryService(uuid: any): Promise<any>
  }
  addEventListener(type: string, listener: (...args: any[]) => void): void
  removeEventListener(type: string, listener: (...args: any[]) => void): void
  watchAdvertisements?: () => Promise<void>
}

class BluetoothPrinter {
  private device: BluetoothDeviceLike | null = null
  private characteristic: any = null
  private listeners: Set<PrinterChangeListener> = new Set()

  get isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!(navigator as any).bluetooth
  }

  get isConnected(): boolean {
    return !!(this.device && this.device.gatt && this.device.gatt.connected && this.characteristic)
  }

  get deviceName(): string | undefined {
    return this.device ? this.device.name : undefined
  }

  onChange(listener: PrinterChangeListener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emitChange() {
    this.listeners.forEach((fn) =>
      fn({ connected: this.isConnected, deviceName: this.deviceName }),
    )
  }

  private getStoredDevice(): { id: string; name: string } | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch (e) {
      return null
    }
  }

  private saveDevice() {
    if (!this.device) return
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ id: this.device.id, name: this.device.name || '' }),
      )
    } catch (e) {
      /* ignore */
    }
  }

  private clearStoredDevice() {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * 弹出浏览器蓝牙选择器，必须由用户手势触发
   */
  async connect(): Promise<void> {
    if (!this.isSupported) {
      throw new Error('当前浏览器不支持 Web Bluetooth，请使用 Chrome 或 Edge')
    }

    const bluetooth: any = (navigator as any).bluetooth

    const device: BluetoothDeviceLike = await bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: COMMON_SERVICE_UUIDS,
    })

    await this.bindDevice(device)
  }

  /**
   * 尝试使用已保存的设备直接重连（需要 Chrome 85+ 支持 getDevices）
   */
  async reconnect(): Promise<boolean> {
    if (!this.isSupported) return false
    const stored = this.getStoredDevice()
    if (!stored) return false

    try {
      const bluetooth: any = (navigator as any).bluetooth
      if (typeof bluetooth.getDevices !== 'function') return false
      const devices: BluetoothDeviceLike[] = await bluetooth.getDevices()
      const target = devices.find((d) => d.id === stored.id)
      if (!target) return false
      await this.bindDevice(target)
      return true
    } catch (e) {
      return false
    }
  }

  private async bindDevice(device: BluetoothDeviceLike) {
    this.device = device
    device.addEventListener('gattserverdisconnected', this.handleDisconnected)

    const server = await device.gatt!.connect()
    const services: any[] = await server.getPrimaryServices()

    let writable: any = null
    for (const service of services) {
      const chars: any[] = await service.getCharacteristics()
      for (const ch of chars) {
        if (ch.properties && (ch.properties.write || ch.properties.writeWithoutResponse)) {
          writable = ch
          break
        }
      }
      if (writable) break
    }

    if (!writable) {
      device.gatt!.disconnect()
      this.device = null
      throw new Error('未找到可写入的打印服务特征，请确认是否为热敏打印机')
    }

    this.characteristic = writable
    this.saveDevice()
    this.emitChange()
  }

  private handleDisconnected = () => {
    this.characteristic = null
    this.emitChange()
  }

  async disconnect(forget = false): Promise<void> {
    if (this.device) {
      this.device.removeEventListener('gattserverdisconnected', this.handleDisconnected)
      try {
        if (this.device.gatt && this.device.gatt.connected) {
          this.device.gatt.disconnect()
        }
      } catch (e) {
        /* ignore */
      }
    }
    this.device = null
    this.characteristic = null
    if (forget) this.clearStoredDevice()
    this.emitChange()
  }

  /**
   * 按 MTU 分包写入
   */
  async write(bytes: Uint8Array): Promise<void> {
    if (!this.isConnected) {
      const ok = await this.reconnect()
      if (!ok || !this.isConnected) {
        throw new Error('打印机未连接')
      }
    }

    const ch = this.characteristic
    const useWithoutResponse = !!(ch.properties && ch.properties.writeWithoutResponse)

    for (let i = 0; i < bytes.length; i += MAX_CHUNK_SIZE) {
      const chunk = bytes.slice(i, i + MAX_CHUNK_SIZE)
      if (useWithoutResponse && typeof ch.writeValueWithoutResponse === 'function') {
        await ch.writeValueWithoutResponse(chunk)
      } else {
        await ch.writeValue(chunk)
      }
    }
  }
}

const printer = new BluetoothPrinter()
export default printer
